// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Cost history — a searchable lookup of what you've actually priced, with
// context. Deliberately NOT averaged: blending unit costs across projects of
// different scope produces a misleading number. Instead we surface every
// instance as its own row (cost + project + size + date + cost status) so the
// estimator judges comparability and grabs the right one.
//
// Pure functions; no React, no DB. Operates on already-loaded project state.

import { ALL_DIVISIONS } from "../projectData";

type AnyItemState = Record<string, {
  description?: string; unit?: string; unitCost?: number; qty?: number;
  included?: boolean; altType?: string; costStatus?: string;
}>;

const DIV_LOOKUP: Record<string, { code: string; name: string }> = Object.fromEntries(
  ALL_DIVISIONS.map((d) => [d.id, { code: d.code, name: d.name }]),
);
const DIV_ORDER = ALL_DIVISIONS.map((d) => d.id);

// Units that are project-specific lump sums or durations, not reusable unit
// rates — looking up "$X per LS" or "per Month" across projects is noise.
// Hidden by default in the view; everything else (SF/LF/SY/EA/CY/…) is a rate.
export const NON_RATE_UNITS = new Set(["ls", "allowance", "%", "days", "weeks", "months", ""]);
export const isRateUnit = (unit: string) => !NON_RATE_UNITS.has((unit || "").trim().toLowerCase());

export interface CostObservation {
  description: string;
  unit: string;
  unitCost: number;
  qty: number;
  costStatus: string;   // "Hard Bid" | "Allowance" | "Needs Review" | …
  projectId: string;
  projectName: string;
  projectArea: number;  // building SF, for judging project scale (0 if unknown)
  dateISO: string;
  divisionId: string;
}

function walkItemIds(rows: any[]): string[] {
  const ids: string[] = [];
  for (const row of rows || []) {
    if (row.type === "item" || (!row.type && row.id)) ids.push(row.id);
    if (row.rows) ids.push(...walkItemIds(row.rows));
    if (row.children) ids.push(...walkItemIds(row.children));
  }
  return ids;
}

/** Pull every priced, included line item from one project as a contextual row. */
export function extractCostObservations(project: {
  id: string;
  name: string;
  dateISO: string;
  buildingArea?: number;
  divisions?: any[];
  itemState?: AnyItemState | null;
}): CostObservation[] {
  const out: CostObservation[] = [];
  const itemState = project.itemState || {};
  const area = Number(project.buildingArea) || 0;
  for (const div of project.divisions || []) {
    for (const sub of div.subcategories || []) {
      for (const id of walkItemIds(sub.rows || [])) {
        const li = itemState[id];
        if (!li || !li.included) continue;
        if (li.altType && li.altType !== "Alt") continue; // skip voids; alts are real scope
        const unitCost = Number(li.unitCost) || 0;
        const description = (li.description || "").trim();
        const unit = (li.unit || "").trim();
        if (unitCost <= 0 || !description || !unit) continue;
        out.push({
          description, unit, unitCost,
          qty: Number(li.qty) || 0,
          costStatus: li.costStatus || "",
          projectId: project.id,
          projectName: project.name,
          projectArea: area,
          dateISO: project.dateISO,
          divisionId: div.id,
        });
      }
    }
  }
  return out;
}

/** All observations across many loaded projects (sorted item, then newest first). */
export function computeCostObservations(
  projects: Parameters<typeof extractCostObservations>[0][],
): CostObservation[] {
  const all = projects.flatMap((p) => extractCostObservations(p));
  all.sort((a, b) =>
    a.description.localeCompare(b.description) ||
    (Date.parse(b.dateISO) || 0) - (Date.parse(a.dateISO) || 0),
  );
  return all;
}

export interface ObservationGroup {
  divisionId: string;
  divisionCode: string;
  divisionName: string;
  observations: CostObservation[];
}

/** Group observations by CSI division (MasterFormat order). Within a division,
 *  same-item instances cluster together, newest first. */
export function groupObservationsByDivision(obs: CostObservation[]): ObservationGroup[] {
  const byDiv = new Map<string, CostObservation[]>();
  for (const o of obs) (byDiv.get(o.divisionId) ?? byDiv.set(o.divisionId, []).get(o.divisionId)!).push(o);
  const groups: ObservationGroup[] = [];
  for (const [divisionId, list] of byDiv) {
    list.sort((a, b) =>
      a.description.localeCompare(b.description) ||
      (Date.parse(b.dateISO) || 0) - (Date.parse(a.dateISO) || 0),
    );
    const meta = DIV_LOOKUP[divisionId];
    groups.push({ divisionId, divisionCode: meta?.code || "—", divisionName: meta?.name || "Other", observations: list });
  }
  groups.sort((a, b) => {
    const ia = DIV_ORDER.indexOf(a.divisionId), ib = DIV_ORDER.indexOf(b.divisionId);
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });
  return groups;
}
